import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ASSETS } from '../components/Navbar';
import { Mail, Lock, User, ArrowRight, Loader2 } from 'lucide-react';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 lg:px-12 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4 mb-16">
            <img 
              src={ASSETS.logo}
              alt="Soul Mate Dates"
              className="h-16 w-auto logo-glow object-contain"
            />
          </Link>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-serif text-[#F5F0E6] mb-3">
            {isLogin ? t('login_welcome') : t('register_title')}
          </h1>
          <p className="text-[#8A8A8A] mb-10">
            {isLogin ? t('login_subtitle') : t('register_subtitle')}
          </p>

          {/* Google Login */}
          <button
            onClick={loginWithGoogle}
            className="w-full btn-outline-wine flex items-center justify-center gap-3 mb-8"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('login_google')}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-[1px] bg-[#8B1A1A]/20" />
            <span className="text-[#8A8A8A] text-sm uppercase tracking-widest">o</span>
            <div className="flex-1 h-[1px] bg-[#8B1A1A]/20" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {!isLogin && (
              <div>
                <label className="label-premium block mb-3">{t('register_name')}</label>
                <div className="relative">
                  <User className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={18} />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-premium pl-8"
                    placeholder={t('field_name')}
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="label-premium block mb-3">{t('login_email')}</label>
              <div className="relative">
                <Mail className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-premium pl-8"
                  placeholder="tu@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="label-premium block mb-3">{t('login_password')}</label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={18} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-premium pl-8"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-900/20 border border-red-900/50 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-wine flex items-center justify-center gap-2"
              data-testid="login-form-submit-button"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  {isLogin ? t('login_submit') : t('register_submit')}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Toggle */}
          <p className="mt-10 text-center text-[#8A8A8A]">
            {isLogin ? t('login_no_account') : t('login_has_account')}{' '}
            <button
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-[#8B1A1A] hover:text-[#8B3A44] transition-colors"
            >
              {isLogin ? t('login_register') : t('login_login')}
            </button>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src={ASSETS.socialEvent}
          alt="Experiencia premium"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] via-transparent to-transparent" />
        
        {/* Quote overlay */}
        <div className="absolute bottom-20 left-10 right-10">
          <p className="text-[#8B1A1A] font-serif text-2xl italic">
            "{t('login_quote')}"
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
